import { Link } from 'react-router-dom';
import { Project } from '../../../../domain/projects/project';
import DateField from '../../../shared/components/forms/DateField';
import TextField from '../../../shared/components/forms/TextField';
import NumberField from '../../../shared/components/forms/NumberField';
import { useEffect, useState } from 'react';
import ProjectStatusSelector from '../selectors/ProjectStatusSelector';

interface Props {
    project: Project;
    isNewProject: boolean;
    onChange: (
        name: string,
        value: string | boolean | number[] | number | undefined
    ) => void;
}

export default function ProjectExecutionSection({
    project,
    isNewProject,
    onChange: handleInputChange,
}: Props) {
    const [executionMonths, setExecutionMonths] = useState(0);
    const [overdueMonths, setOverdueMonths] = useState(0);
    const [isRealEndDate, setIsRealEndDate] = useState(false);

    const isValidDate = (date: Date | null | undefined) => {
        return date instanceof Date && !isNaN(date.getTime());
    };

    const calculateMonths = () => {
        const startDate = new Date(project.startDate);
        const endDate = new Date(
            project.scheduledDate === null ? '' : project.scheduledDate
        );
        const realEndDate = new Date(
            project.realEndDate === null ? '' : project.realEndDate
        );
        const currentDate = new Date();

        if (isValidDate(realEndDate)) {
            const differenceMonthsReal = calculateTotalMonths(
                startDate,
                realEndDate
            );
            const overdueReal = calculateMonthDifference(endDate, realEndDate);
            setExecutionMonths(differenceMonthsReal);
            setOverdueMonths(overdueReal);
            setIsRealEndDate(true);
        } else if (isValidDate(startDate) && isValidDate(endDate)) {
            const differenceMonths = calculateTotalMonths(startDate, currentDate);
            const overdue = calculateMonthDifference(endDate, currentDate);

            setExecutionMonths(differenceMonths);
            setOverdueMonths(overdue);
            setIsRealEndDate(false);
        }
    };

    useEffect(() => {
        calculateMonths();
    }, [project.startDate, project.endDate, project.realEndDate]);

    const calculateMonthDifference = (date1: Date, date2: Date) => {
        const diffYears = date2.getFullYear() - date1.getFullYear();
        const diffMonths = date2.getMonth() - date1.getMonth();
        return diffYears * 12 + diffMonths;
    };

    const calculateTotalMonths = (date1: Date, date2: Date) => {
        const diffYears = date2.getFullYear() - date1.getFullYear();
        const diffMonths = date2.getMonth() - date1.getMonth();
        return diffYears * 12 + diffMonths;
    };

    const handleInputChangeAndUpdate = (
        name: string,
        value: string | boolean | number[] | number | undefined
    ) => {
        handleInputChange(name, value);
        calculateMonths();
    };

    return (
        <>
            <h5 className='my-3'>Ejecución de Proyecto</h5>

            <div className='col-4'>
                <DateField
                    label='Fecha Inicial'
                    name='startDate'
                    onChange={handleInputChange}
                    required={false}
                    value={project.startDate}
                    key='startDate'
                />
            </div>
            <div className='col-4'>
                <DateField
                    label='Fecha Cierre CRO'
                    name='scheduledDate'
                    onChange={handleInputChange}
                    required={false}
                    value={project.scheduledDate}
                    key='scheduledDate'
                />
            </div>
            <div className='col-4'>
                <DateField
                    label='Fecha Final Seguimiento'
                    name='endDate'
                    onChange={handleInputChange}
                    required={false}
                    value={project.endDate}
                    key='endDate'
                />
            </div>
            <div className='col-4'>
                <DateField
                    label='Fecha Final Real'
                    name='realEndDate'
                    onChange={handleInputChange}
                    required={false}
                    value={project.realEndDate}
                    key='realEndDate'
                    isDisabled={isNewProject}
                />
            </div>
            <div className='col-4'>
                <ProjectStatusSelector
                    value={project.projectStatusId}
                    name='projectStatusId'
                    onChange={handleInputChange}
                    isDisabled={true}
                />
            </div>
            <div className='col-2'>
                <NumberField
                    label='Avance %'
                    name='percentage'
                    onChange={handleInputChange}
                    required={true}
                    value={project.percentage}
                    key='percentage'
                />
            </div>
            <div className='col-3'>
                <TextField
                    label='Meses Ejecución'
                    name='executionMonths'
                    onChange={handleInputChangeAndUpdate}
                    required={true}
                    isDisabled={isNewProject || isRealEndDate}
                    value={executionMonths}
                    key='execution'
                />
            </div>
            <div className='col-3'>
                <TextField
                    label='Meses Atraso'
                    name='overdueMonths'
                    onChange={handleInputChangeAndUpdate}
                    required={true}
                    isDisabled={isNewProject || isRealEndDate}
                    value={overdueMonths > 0 ? overdueMonths : 0}
                    key='overdue'
                />
            </div>
        </>
    );
}
